/**
 * The catalog, as a spreadsheet.
 *
 * A shop's price list already exists — in Excel, in a supplier export, in a
 * file somebody maintains. Asking them to retype it into a web form to try the
 * product is asking them not to try the product. So: paste or drop a CSV, see
 * what will be imported, then commit it.
 *
 * The parser is forgiving about everything that does not change meaning —
 * column order, capitalisation, spaces in headers, `$` and thousands
 * separators, quoted fields, blank lines, a UTF-8 BOM from Excel — and strict
 * about everything that does. A row with a price of "call for quote" is
 * reported, never guessed at and never silently dropped, because a part that
 * vanishes on import becomes a part missing from an estimate.
 */

import type { CatalogEntry } from './build.js';

export type PartType = CatalogEntry['partType'];

/** Header spellings a real shop file actually uses. */
const ALIASES: Record<string, string> = {
  'part': 'partNumber', 'partno': 'partNumber', 'partnumber': 'partNumber',
  'partno.': 'partNumber', 'part#': 'partNumber', 'number': 'partNumber',
  'sku': 'partNumber', 'oem': 'partNumber',
  'description': 'description', 'desc': 'description', 'name': 'description',
  'part description': 'description', 'item': 'description',
  'type': 'partType', 'parttype': 'partType', 'kind': 'partType',
  'price': 'price', 'cost': 'price', 'listprice': 'price', 'unitprice': 'price',
  'amount': 'price', 'each': 'price',
  // The shop's own times. All optional - a catalog of prices alone still works.
  'replacehours': 'replaceHours', 'replace': 'replaceHours', 'rr': 'replaceHours',
  'r&r': 'replaceHours', 'randr': 'replaceHours', 'replacetime': 'replaceHours',
  'repairhours': 'repairHours', 'repair': 'repairHours', 'dent': 'repairHours',
  'denthours': 'repairHours', 'repairtime': 'repairHours', 'bodyhours': 'repairHours',
  'refinishhours': 'refinishHours', 'refinish': 'refinishHours',
  'paint': 'refinishHours', 'painthours': 'refinishHours',
  'refinishtime': 'refinishHours', 'painthrs': 'refinishHours',
  // What the row applies to. Blank means "anything".
  'bodystyle': 'bodyStyle', 'body': 'bodyStyle', 'style': 'bodyStyle',
  'vehicletype': 'bodyStyle', 'cartype': 'bodyStyle',
  'make': 'make', 'brand': 'make', 'marque': 'make', 'manufacturer': 'make',
};

/** Body-style spellings a shop would actually type. '' means "any vehicle". */
const STYLES: Record<string, string> = {
  'sedan': 'sedan', 'saloon': 'sedan', 'car': 'sedan', 'hatchback': 'sedan',
  'coupe': 'sedan', 'wagon': 'sedan', 'estate': 'sedan',
  'suv': 'suv', 'crossover': 'suv', 'cuv': 'suv', '4x4': 'suv', 'jeep': 'suv',
  'pickup': 'pickup', 'truck': 'pickup', 'ute': 'pickup', 'pick up': 'pickup',
  'van': 'van', 'minivan': 'van', 'mpv': 'van', 'panel van': 'van',
  'other': 'other', 'any': '', 'all': '', '': '',
};

/**
 * An hours cell. Blank is NOT zero.
 *
 * Blank means "nobody has said", which the app asks about. Zero would mean
 * "this takes no time", which would quietly put a free line on an estimate.
 */
function parseHours(raw: string): number | null | 'bad' {
  const t = raw.trim();
  if (!t) return null;
  const v = Number(t.replace(/\s*(hrs?|hours?)\s*$/i, '').replace(',', '.'));
  if (!Number.isFinite(v) || v < 0 || v > 200) return 'bad';
  return v;
}

/**
 * Part-type spellings, mapped to what the calculator understands.
 *
 * `reconditioned` is deliberately ABSENT. A reman part is not a used part —
 * different price, different warranty — and the calculator has no category for
 * it, so mapping it to something adjacent would put a quietly wrong figure on
 * an estimate. A file that uses it is told so and asked to choose.
 */
const TYPES: Record<string, PartType> = {
  'new_oem': 'new_oem', 'newoem': 'new_oem', 'oem': 'new_oem', 'new': 'new_oem',
  'oe': 'new_oem', 'genuine': 'new_oem',
  'aftermarket': 'aftermarket', 'am': 'aftermarket', 'a/m': 'aftermarket',
  'after market': 'aftermarket', 'certified': 'aftermarket', 'capa': 'aftermarket',
  'used': 'used', 'recycled': 'used', 'lkq': 'used', 'salvage': 'used',
  'existing': 'existing', 'repair': 'existing', 'existing part': 'existing',
  'sublet': 'sublet', 'outsourced': 'sublet', 'subcontract': 'sublet',
  'none': 'none', 'labour only': 'none', 'labor only': 'none',
  'labour': 'none', 'labor': 'none', '': 'none',
};

/** Named in the error and on screen, so the two can never disagree. */
export const PART_TYPES: PartType[] =
  ['new_oem', 'aftermarket', 'used', 'existing', 'sublet', 'none'];

export interface ParsedRow {
  /** 1-based line in the file, so a complaint can be acted on. */
  line: number;
  entry?: CatalogEntry;
  /** Why this row cannot be imported. */
  problem?: string;
}

export interface ParseResult {
  rows: ParsedRow[];
  ok: CatalogEntry[];
  problems: ParsedRow[];
  /** Headers that were not recognised, reported rather than ignored. */
  ignoredColumns: string[];
}

/**
 * Split one CSV line, honouring quotes.
 *
 * Hand-rolled because a description like `Bumper cover, front` is ordinary in
 * this data and a naive split on commas would shear it in half — silently
 * shifting the price into the type column.
 */
export function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let quoted = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i]!;
    if (quoted) {
      if (c === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; } else { quoted = false; }
      } else cur += c;
    } else if (c === '"') quoted = true;
    else if (c === ',' || c === ';' || c === '\t') { out.push(cur); cur = ''; }
    else cur += c;
  }
  out.push(cur);
  return out.map((v) => v.trim());
}

/** `$1,234.50` / `1 234,50` / `(12.00)` -> a number, or null if it is not one. */
export function parsePrice(raw: string): number | null {
  const t = raw.trim();
  if (!t) return null;
  const negative = /^\(.*\)$/.test(t);
  let v = t.replace(/[()]/g, '').replace(/[$£€\s]/g, '');
  // European decimal comma, but only when there is no dot to disagree with it.
  if (/,\d{1,2}$/.test(v) && !v.includes('.')) v = v.replace(',', '.');
  v = v.replace(/,/g, '');
  if (!/^-?\d*\.?\d+$/.test(v)) return null;
  const n = Number(v);
  if (!Number.isFinite(n)) return null;
  return negative ? -n : n;
}

export function parseCatalogCsv(text: string): ParseResult {
  // Excel writes a BOM; left in place it makes the first header unmatchable
  // and every row lose its part number, which looks like a broken import.
  const clean = text.replace(/^﻿/, '').replace(/\r\n?/g, '\n');
  const lines = clean.split('\n');

  let headerIdx = lines.findIndex((l) => l.trim() !== '');
  if (headerIdx === -1) {
    return { rows: [], ok: [], problems: [], ignoredColumns: [] };
  }

  const rawHeaders = splitCsvLine(lines[headerIdx]!);
  const map: Array<string | null> = rawHeaders.map(
    (h) => ALIASES[h.toLowerCase().replace(/[\s_]+/g, '')] ?? ALIASES[h.toLowerCase()] ?? null);
  const ignoredColumns = rawHeaders.filter((_, i) => map[i] === null && rawHeaders[i] !== '');

  const rows: ParsedRow[] = [];
  for (let i = headerIdx + 1; i < lines.length; i++) {
    const line = lines[i]!;
    if (line.trim() === '') continue;
    const cells = splitCsvLine(line);
    const get = (field: string) => {
      const at = map.indexOf(field);
      return at === -1 ? '' : (cells[at] ?? '');
    };

    const description = get('description');
    const priceRaw = get('price');
    const typeRaw = get('partType').toLowerCase().replace(/[\s_-]+/g, ' ').trim();

    if (!description) {
      rows.push({ line: i + 1, problem: 'No description — nothing to price.' });
      continue;
    }
    const price = parsePrice(priceRaw);
    if (price === null) {
      rows.push({ line: i + 1,
        problem: `Price "${priceRaw || '(empty)'}" is not a number.` });
      continue;
    }
    if (price < 0) {
      rows.push({ line: i + 1, problem: `Price ${priceRaw} is negative.` });
      continue;
    }
    const partType = TYPES[typeRaw] ?? TYPES[typeRaw.replace(/ /g, '')] ?? null;
    if (partType === null) {
      rows.push({ line: i + 1,
        problem: `Part type "${typeRaw}" is not one I know. `
          + `Use one of: ${PART_TYPES.join(', ')}.` });
      continue;
    }

    const styleRaw = get('bodyStyle').toLowerCase().replace(/[_-]+/g, ' ').trim();
    const bodyStyle = STYLES[styleRaw] ?? STYLES[styleRaw.replace(/ /g, '')];
    if (bodyStyle === undefined) {
      rows.push({ line: i + 1,
        problem: `Body style "${styleRaw}" is not one I know. `
          + 'Use sedan, suv, pickup, van, other - or leave blank for any.' });
      continue;
    }

    const hours: Record<string, number> = {};
    let hoursProblem: string | null = null;
    for (const field of ['replaceHours', 'repairHours', 'refinishHours'] as const) {
      const parsed = parseHours(get(field));
      if (parsed === 'bad') {
        hoursProblem = `${field.replace('Hours', '')} hours "${get(field)}" `
          + 'is not a number.';
        break;
      }
      if (parsed !== null) hours[field] = parsed;
    }
    if (hoursProblem) {
      rows.push({ line: i + 1, problem: hoursProblem });
      continue;
    }

    const partNumber = get('partNumber');
    const make = get('make').trim();
    rows.push({
      line: i + 1,
      entry: {
        description, partType, price,
        ...(partNumber ? { partNumber } : {}),
        ...(bodyStyle ? { bodyStyle: bodyStyle as never } : {}),
        ...(make ? { make } : {}),
        ...hours,
      },
    });
  }

  return {
    rows,
    ok: rows.filter((r) => r.entry).map((r) => r.entry!),
    problems: rows.filter((r) => r.problem),
    ignoredColumns,
  };
}

/** One CSV cell, quoted only when it has to be. */
function cell(v: string): string {
  return /[",;\t\n]/.test(v) ? `"${v.replace(/"/g, '""')}"` : v;
}

/**
 * The catalog as a file, in the same shape the importer accepts.
 *
 * Round-tripping matters: export, edit in Excel, import back. A format that
 * only goes one way is a trap disguised as a feature.
 */
export function toCatalogCsv(rows: CatalogEntry[]): string {
  const head = 'part_number,description,body_style,make,part_type,price,'
    + 'replace_hours,repair_hours,refinish_hours';
  // Absent hours export as EMPTY, not 0 - re-importing a 0 would turn
  // "nobody has said" into "this takes no time" and zero a labour line.
  const hr = (v?: number) => (v === undefined || v === null ? '' : String(v));
  const body = rows.map((r) => [
    cell(r.partNumber ?? ''), cell(r.description),
    r.bodyStyle ?? '', cell(r.make ?? ''), r.partType, String(r.price),
    hr(r.replaceHours), hr(r.repairHours), hr(r.refinishHours),
  ].join(','));
  return [head, ...body].join('\n') + '\n';
}

/** Shown on screen so nobody has to guess the format. */
export const SAMPLE_CSV = [
  'part_number,description,body_style,make,part_type,price,'
    + 'replace_hours,repair_hours,refinish_hours',
  '68321-A2,front bumper cover,sedan,Toyota,new_oem,412.00,2.0,2.5,2.2',
  '71121-B4,hood,sedan,Toyota,new_oem,690.00,1.8,3.5,2.8',
  '88420-T1,hood,pickup,Toyota,new_oem,940.00,2.2,4.5,3.6',
  ',"grille, upper",,,aftermarket,168.50,0.6,,0.4',
  ',wheel alignment,,,sublet,120,,,',
  '',
].join('\n');
